import React, { useState, useCallback, useEffect } from 'react';
import { importCompleteProject } from '../../utilities/projectImporter';
import { exportCompleteProject } from '../../utilities/projectExporter';
import './ProjectManager.css';

const ProjectManager = ({ 
  onProjectCreate, 
  onProjectLoad, 
  onProjectSave, 
  onProjectDelete,
  onProjectExport,
  onProjectImport,
  currentProject,
  language,
  translations 
}) => {
  const t = translations || {};
  const [projects, setProjects] = useState([]);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);

  // تحميل قائمة المشاريع من localStorage
  const loadProjects = useCallback(() => {
    try {
      const projectsList = JSON.parse(localStorage.getItem('cad_projects_list') || '[]');
      setProjects(projectsList);
      
      // تحديد المشروع النشط
      const lastProjectId = localStorage.getItem('cad_last_project_id');
      if (lastProjectId && projectsList.find(p => p.id === lastProjectId)) {
        setSelectedProjectId(lastProjectId);
      }
    } catch (error) {
      console.error('خطأ في تحميل قائمة المشاريع:', error);
      setProjects([]);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // إنشاء مشروع جديد
  const createNewProject = useCallback(() => {
    if (!newProjectName.trim()) {
      alert(t.enterProjectNameAlert || 'يرجى إدخال اسم للمشروع');
      return;
    }

    const projectId = `project_${Date.now()}`;
    const newProject = {
      id: projectId,
      name: newProjectName.trim(),
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString(),
      description: '',
      version: '1.0.0' // إضافة الإصدار الأولي
    };

    try {
      // تحديث قائمة المشاريع
      const updatedProjects = [...projects, newProject];
      setProjects(updatedProjects);
      localStorage.setItem('cad_projects_list', JSON.stringify(updatedProjects));
      
      // تحديد المشروع الجديد كمشروع نشط
      setSelectedProjectId(projectId);
      localStorage.setItem('cad_last_project_id', projectId);
      
      // إنشاء المشروع الجديد
      if (onProjectCreate) {
        onProjectCreate(newProject);
      }

      setShowNewProjectModal(false);
      setNewProjectName('');
    } catch (error) {
      console.error('خطأ في إنشاء المشروع:', error);
      alert(t.createProjectFailed || 'فشل في إنشاء المشروع');
    }
  }, [newProjectName, projects, onProjectCreate, t]);

  // تحميل مشروع موجود
  const loadProject = useCallback((projectId) => {
    try {
      const projectData = localStorage.getItem(`cad_project:${projectId}`);
      if (!projectData) {
        alert(t.noProjectData || 'لا توجد بيانات للمشروع المحدد');
        return;
      }

      const parsedData = JSON.parse(projectData);
      setSelectedProjectId(projectId);
      localStorage.setItem('cad_last_project_id', projectId);
      
      if (onProjectLoad) {
        onProjectLoad(parsedData);
      }
    } catch (error) {
      console.error('خطأ في تحميل المشروع:', error);
      alert(t.loadProjectFailed || 'فشل في تحميل المشروع');
    }
  }, [onProjectLoad, t]);

  // حفظ المشروع الحالي
  const saveCurrentProject = useCallback(() => {
    if (!selectedProjectId) {
      alert(t.noProjectSelected || 'لا يوجد مشروع محدد للحفظ');
      return;
    }

    if (onProjectSave) {
      onProjectSave(selectedProjectId);
    }

    // تحديث وقت التعديل والإصدار
    const updatedProjects = projects.map(p => {
      if (p.id === selectedProjectId) {
        // تحديث الإصدار (زيادة الرقم الفرعي)
        const versionParts = (p.version || '1.0.0').split('.').map(Number);
        versionParts[2] = (versionParts[2] || 0) + 1;
        const newVersion = versionParts.join('.');
        
        return { 
          ...p, 
          lastModified: new Date().toISOString(),
          version: newVersion
        };
      }
      return p;
    });
    setProjects(updatedProjects);
    localStorage.setItem('cad_projects_list', JSON.stringify(updatedProjects));
  }, [selectedProjectId, projects, onProjectSave, t]);

  // حذف مشروع
  const deleteProject = useCallback((projectId) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    if (!window.confirm(`${t.confirmDelete || 'هل أنت متأكد من حذف المشروع'} "${project.name}"؟`)) {
      return;
    }

    try {
      // حذف بيانات المشروع
      localStorage.removeItem(`cad_project:${projectId}`);
      
      // تحديث قائمة المشاريع
      const updatedProjects = projects.filter(p => p.id !== projectId);
      setProjects(updatedProjects);
      localStorage.setItem('cad_projects_list', JSON.stringify(updatedProjects));
      
      // إذا كان المشروع المحذوف هو النشط، إلغاء التحديد
      if (selectedProjectId === projectId) {
        setSelectedProjectId(null);
        localStorage.removeItem('cad_last_project_id');
        
        if (onProjectDelete) {
          onProjectDelete();
        }
      }
    } catch (error) {
      console.error('خطأ في حذف المشروع:', error);
      alert(t.deleteProjectFailed || 'فشل في حذف المشروع');
    }
  }, [projects, selectedProjectId, onProjectDelete, t]);

  // تصدير مشروع إلى ملف
  const exportProject = useCallback((projectId) => {
    try {
      const project = projects.find(p => p.id === projectId);
      if (!project) {
        alert(t.noProjectData || 'لا توجد بيانات للمشروع المحدد');
        return;
      }

      // استخدام أداة التصدير الجديدة
      const result = exportCompleteProject(projectId, project.name);
      
      if (result?.success) {
        // إعادة تحميل المشاريع لإظهار الإصدار المحدث
        setTimeout(() => {
          loadProjects();
        }, 100);
        
        if (onProjectExport) {
          onProjectExport({ projectId, fileName: result.fileName });
        }
      }
      
    } catch (error) {
      console.error('خطأ في تصدير المشروع:', error);
      alert(t.exportProjectFailed || 'فشل في تصدير المشروع');
    }
  }, [projects, loadProjects, onProjectExport, t]);

  // استيراد مشروع من ملف
  const importProject = useCallback((file) => {
    if (!file) return;

    importCompleteProject(
      file,
      (importResult) => {
        // نجح الاستيراد
        console.log('Import successful:', importResult);
        
        // تحديث قائمة المشاريع
        loadProjects();
        
        // تحديد المشروع المستورد كنشط
        setSelectedProjectId(importResult.projectId);
        
        if (onProjectImport) {
          onProjectImport(importResult.projectData);
        }

        setShowImportModal(false);
        alert(`${t.importSuccess || 'تم استيراد المشروع بنجاح!'}\n${t.importType || 'النوع'}: ${importResult.type}`);
      },
      (errorMessage) => {
        // فشل الاستيراد
        console.error('Import failed:', errorMessage);
        alert(`${t.importFailed || 'فشل في استيراد المشروع'}: ${errorMessage}`);
      }
    );
  }, [onProjectImport, loadProjects, t]);

  const formatDate = (dateString) => {
    try {
      const locale = language === 'ar' ? 'ar-SA' : 'en-US';
      return new Date(dateString).toLocaleString(locale);
    } catch {
      return t.notSpecified || 'غير محدد';
    }
  };

  return (
    <div className="project-manager">
      <div className="project-manager-header">
        <h2>معلومات</h2>
        <div className="project-actions">
          <button 
            className="btn btn-primary"
            onClick={() => setShowNewProjectModal(true)}
          >
            + {t.newProject || 'مشروع جديد'}
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => setShowImportModal(true)}
          >
            📁 {t.importProject || 'استيراد مشروع'}
          </button>
          {selectedProjectId && (
            <button 
              className="btn btn-success"
              onClick={saveCurrentProject}
            >
              💾 {t.saveCurrentProject || 'حفظ المشروع الحالي'}
            </button>
          )}
        </div>
      </div>

      <div className="projects-list">
        <div className="projects-grid">
          {projects.length === 0 ? (
            <div className="empty-state">
              <p>{t.noSavedProjects || 'لا توجد مشاريع محفوظة'}</p>
              <button 
                className="btn btn-primary"
                onClick={() => setShowNewProjectModal(true)}
              >
                {t.createFirstProject || 'إنشاء أول مشروع'}
              </button>
            </div>
          ) : (
            projects.map(project => (
              <div 
                key={project.id} 
                className={`project-card ${selectedProjectId === project.id ? 'active' : ''}`}
              >
                <div className="project-info">
                  <h3>{project.name}</h3>
                  <div className="project-meta">
                    <span className="project-version">{t.version || 'v'}{project.version || '1.0.0'}</span>
                    <p className="project-dates">
                      <small>{t.createdAt || 'تم الإنشاء'}: {formatDate(project.createdAt)}</small>
                      <small>{t.lastModified || 'آخر تعديل'}: {formatDate(project.lastModified)}</small>
                    </p>
                  </div>
                  {project.description && (
                    <p className="project-description">{project.description}</p>
                  )}
                </div>
                
                <div className="project-actions-card">
                  <button 
                    className="btn btn-small btn-primary"
                    onClick={() => loadProject(project.id)}
                    disabled={selectedProjectId === project.id}
                  >
                    {selectedProjectId === project.id ? (t.active || 'نشط') : (t.load || 'تحميل')}
                  </button>
                  <button 
                    className="btn btn-small btn-info"
                    onClick={() => exportProject(project.id)}
                  >
                    {t.export || 'تصدير'}
                  </button>
                  <button 
                    className="btn btn-small btn-danger"
                    onClick={() => deleteProject(project.id)}
                  >
                    {t.delete || 'حذف'}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* نافذة إنشاء مشروع جديد */}
      {showNewProjectModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{t.createNewProject || 'إنشاء مشروع جديد'}</h3>
              <button 
                className="modal-close"
                onClick={() => setShowNewProjectModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t.projectName || 'اسم المشروع'}:</label>
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder={t.enterProjectName || 'أدخل اسم المشروع'}
                  onKeyPress={(e) => e.key === 'Enter' && createNewProject()}
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowNewProjectModal(false)}
              >
                {t.cancel || 'إلغاء'}
              </button>
              <button 
                className="btn btn-primary"
                onClick={createNewProject}
              >
                {t.create || 'إنشاء'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* نافذة استيراد مشروع */}
      {showImportModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{t.importProjectTitle || 'استيراد مشروع'}</h3>
              <button 
                className="modal-close"
                onClick={() => setShowImportModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t.chooseProjectFile || 'اختر ملف المشروع (.cadproject):'}:</label>
                <input
                  type="file"
                  accept=".cadproject,.json"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      importProject(file);
                    }
                  }}
                />
              </div>
              <div className="import-info">
                <p><strong>{t.note || 'ملاحظة'}:</strong> {t.importNote || 'يمكنك استيراد ملفات المشاريع بامتداد .cadproject أو .json'}</p>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowImportModal(false)}
              >
                {t.closeModal || 'إغلاق'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectManager;